import { PromoBadgeLabel } from './PromoBadgeLabel';

import useMartyContext from 'hooks/useMartyContext';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from 'components/tooltip/Tooltip';

interface PromoBadgeStyle {
  backgroundColor: string;
  foregroundColor: string;
}

interface PromoBadgeProps {
  label: string;
  promoCode?: string;
  style: PromoBadgeStyle;
  tooltip?: string;
  isOnSale?: boolean;
  disclaimer?: string;
}

const FOREGROUND_FALLBACK_COLOR = '#000000';
const BACKGROUND_FALLBACK_COLOR = '#FFE337';

export const PromoBadge = ({ disclaimer, style, isOnSale, tooltip, promoCode, label }: PromoBadgeProps) => {
  const { testId } = useMartyContext();

  // Don't render if label is missing (minimum for a default badge to show up)
  if (!label) {
    return null;
  }

  const badgeStyles = {
    backgroundColor: style?.backgroundColor ?? BACKGROUND_FALLBACK_COLOR,
    color: style?.foregroundColor ?? FOREGROUND_FALLBACK_COLOR
  };

  // Core badge element that can be rendered with or without tooltip
  const promoBadge = (
    <span className="inline-block w-fit rounded-sm p-1 text-xs font-normal" style={badgeStyles} role="alert">
      <PromoBadgeLabel label={label} promoCode={promoCode} isOnSale={isOnSale} />
    </span>
  );

  return (
    <div data-test-id={testId('promoBadge')}>
      {tooltip ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>{promoBadge}</TooltipTrigger>
            <TooltipPortal>
              <TooltipContent className="text-black rounded border bg-default-minimal px-3 py-2 text-xs shadow-lg" sideOffset={5}>
                {tooltip}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
        </TooltipProvider>
      ) : (
        promoBadge
      )}
      {disclaimer && (
        <div className="mt-1 text-xs italic" style={{ color: style?.foregroundColor ?? FOREGROUND_FALLBACK_COLOR }}>
          {disclaimer}
        </div>
      )}
    </div>
  );
};
